import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Circle, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  MapPin, Package, Home, Building, Car, Hand, 
  Loader2, AlertCircle, Grid3X3, DollarSign, Users, GraduationCap,
  PieChart, CheckCircle, XCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Grid label marker for preview
const createGridLabelIcon = (label) => new L.DivIcon({
  className: 'grid-label-marker',
  html: `<div style="
    background: rgba(16, 185, 129, 0.85);
    color: white;
    font-weight: bold;
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    white-space: nowrap;
    text-align: center;
  ">${label}</div>`,
  iconSize: [35, 20],
  iconAnchor: [17, 10],
});

const DELIVERY_TYPE_INFO = {
  houses: { label: 'Houses', icon: Home, color: '#8b5cf6' },
  apartments: { label: 'Apartments', icon: Building, color: '#f97316' },
  cars: { label: 'Cars', icon: Car, color: '#3b82f6' },
  hands: { label: 'Hands', icon: Hand, color: '#10b981' },
};

const ZonePreviewPage = () => {
  const { previewId } = useParams();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  useEffect(() => {
    fetchPreview();
  }, [previewId]);

  const fetchPreview = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/zone-preview/${previewId}`);
      if (!response.ok) {
        throw new Error('Preview not found');
      }
      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (approved) => {
    if (!clientName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/public/zone-preview/${previewId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved,
          client_name: clientName,
          client_email: clientEmail
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit');
      }
      
      toast.success(approved ? 'Zone approved! The delivery job is now live.' : 'Zone rejected. Thank you for your feedback.');
      fetchPreview(); // Refresh to show updated status
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getMapCenter = () => {
    if (preview?.circle_center) {
      return preview.circle_center;
    }
    if (preview?.boundary && preview.boundary.length > 0) {
      const lats = preview.boundary.map(p => p[0]);
      const lngs = preview.boundary.map(p => p[1]);
      return [
        (Math.max(...lats) + Math.min(...lats)) / 2,
        (Math.max(...lngs) + Math.min(...lngs)) / 2
      ];
    }
    return [40.7128, -74.0060];
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading delivery zone preview...</p>
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Preview Not Found</h2>
          <p className="text-gray-500">
            This delivery zone preview may have expired or the link is invalid.
          </p>
        </Card>
      </div>
    );
  }

  const typeInfo = DELIVERY_TYPE_INFO[preview.delivery_type] || DELIVERY_TYPE_INFO.houses;
  const TypeIcon = typeInfo.icon;
  const usesCircle = ['cars', 'hands'].includes(preview.delivery_type);
  const demographics = preview.demographics || {};
  const isApproved = preview.approval_status === 'approved';
  const isRejected = preview.approval_status === 'rejected';
  const isPending = preview.approval_status === 'pending';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="zone-preview-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={24} />
            <h1 className="text-2xl font-bold">Delivery Zone Preview</h1>
          </div>
          <p className="text-purple-200">Review the proposed delivery area for your campaign</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        {isApproved && (
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <div>
                <p className="font-semibold text-green-800">Zone Approved</p>
                <p className="text-sm text-green-600">
                  Approved by {preview.approved_by === 'client' ? 'Client' : 'Administrator'} on {new Date(preview.approved_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>
        )}
        
        {isRejected && (
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center gap-3">
              <XCircle className="text-red-600" size={24} />
              <div>
                <p className="font-semibold text-red-800">Zone Rejected</p>
                <p className="text-sm text-red-600">This zone preview was not approved.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Info Card */}
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{preview.client || 'Delivery Zone'}</h2>
              <p className="text-gray-500">{preview.location || 'Location TBD'}</p>
            </div>
            <Badge className="bg-purple-100 text-purple-700 flex items-center gap-1">
              <TypeIcon size={14} />
              {typeInfo.label}
            </Badge>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <Package size={24} className="mx-auto text-purple-500 mb-2" />
              <p className="text-2xl font-bold text-gray-900">{preview.total_deliveries?.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Deliveries</p>
            </div>
            
            {preview.grid_sections && preview.grid_sections.length > 1 && (
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <Grid3X3 size={24} className="mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{preview.grid_sections.length}</p>
                <p className="text-xs text-gray-500">Grid Sections</p>
              </div>
            )}
            
            {usesCircle && preview.circle_radius && (
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <MapPin size={24} className="mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold text-gray-900">{preview.circle_radius} mi</p>
                <p className="text-xs text-gray-500">Radius</p>
              </div>
            )}
          </div>
        </Card>

        {/* Demographics Card */}
        {demographics && Object.keys(demographics).length > 0 && (
          <Card className="border-0 shadow-sm" data-testid="demographics-card">
            <div className="p-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <PieChart size={18} className="text-indigo-500" />
                Area Demographics
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Source: {demographics.source || 'US Census Bureau'}
              </p>
            </div>
            <div className="p-4 space-y-6">
              {/* Income & Home Value */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={20} className="text-green-600" />
                    <span className="font-medium text-gray-700">Median Household Income</span>
                  </div>
                  <p className="text-3xl font-bold text-green-700">
                    {formatCurrency(demographics.median_household_income)}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Home size={20} className="text-blue-600" />
                    <span className="font-medium text-gray-700">Average Home Value</span>
                  </div>
                  <p className="text-3xl font-bold text-blue-700">
                    {formatCurrency(demographics.median_home_value)}
                  </p>
                </div>
              </div>

              {/* Ethnicity Distribution */}
              {demographics.ethnicity && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={18} className="text-purple-600" />
                    <span className="font-medium text-gray-700">Population by Ethnicity</span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { key: 'white', label: 'White', color: 'bg-blue-500' },
                      { key: 'black', label: 'Black', color: 'bg-purple-500' },
                      { key: 'hispanic', label: 'Hispanic', color: 'bg-orange-500' },
                      { key: 'asian', label: 'Asian', color: 'bg-green-500' },
                      { key: 'other', label: 'Other', color: 'bg-gray-500' },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="text-center">
                        <div className={`h-24 ${color} rounded-t-lg relative overflow-hidden`}>
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-white/30"
                            style={{ height: `${100 - (demographics.ethnicity[key] || 0)}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-lg">
                            {demographics.ethnicity[key]?.toFixed(0) || 0}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Age Distribution */}
              {demographics.age_distribution && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={18} className="text-indigo-600" />
                    <span className="font-medium text-gray-700">Age Distribution</span>
                  </div>
                  <div className="flex gap-2">
                    {[
                      { key: 'under_18', label: 'Under 18', color: 'bg-cyan-500' },
                      { key: '18_34', label: '18-34', color: 'bg-blue-500' },
                      { key: '35_54', label: '35-54', color: 'bg-purple-500' },
                      { key: '55_64', label: '55-64', color: 'bg-orange-500' },
                      { key: '65_plus', label: '65+', color: 'bg-red-500' },
                    ].map(({ key, label, color }) => (
                      <div key={key} className="flex-1 text-center">
                        <div className={`${color} rounded-lg py-3 px-2`}>
                          <span className="text-white font-bold">
                            {demographics.age_distribution[key]?.toFixed(0) || 0}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Education Levels */}
              {demographics.education && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap size={18} className="text-amber-600" />
                    <span className="font-medium text-gray-700">Education Levels</span>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { key: 'high_school_or_less', label: 'Less than HS', icon: '📚' },
                      { key: 'high_school_diploma', label: 'High School', icon: '🎓' },
                      { key: 'bachelors_degree', label: "Bachelor's", icon: '🎓' },
                      { key: 'graduate_degree', label: 'Graduate+', icon: '🎓' },
                    ].map(({ key, label, icon }) => (
                      <div key={key} className="bg-amber-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-700">
                          {demographics.education[key]?.toFixed(0) || 0}%
                        </p>
                        <p className="text-xs text-gray-600">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Map */}
        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <MapPin size={18} className="text-purple-500" />
              Full Delivery Zone Map
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {preview.grid_sections && preview.grid_sections.length > 1 
                ? `This zone will be divided into ${preview.grid_sections.length} sub-jobs for efficient delivery`
                : 'Complete delivery area for this campaign'}
            </p>
          </div>
          <div className="h-[500px]">
            <MapContainer
              center={getMapCenter()}
              zoom={14}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              
              {/* Main boundary polygon */}
              {preview.boundary && preview.boundary.length > 0 && (
                <Polygon
                  positions={preview.boundary}
                  pathOptions={{ 
                    color: typeInfo.color, 
                    fillColor: typeInfo.color, 
                    fillOpacity: 0.15,
                    weight: 3,
                    dashArray: '10, 5'
                  }}
                />
              )}
              
              {/* Circle for cars/hands */}
              {usesCircle && preview.circle_center && (
                <Circle
                  center={preview.circle_center}
                  radius={(preview.circle_radius || 1) * 1609.34}
                  pathOptions={{ 
                    color: typeInfo.color, 
                    fillColor: typeInfo.color, 
                    fillOpacity: 0.15,
                    weight: 3
                  }}
                />
              )}
              
              {/* Grid section labels (without grid boundaries for cleaner preview) */}
              {preview.grid_sections && preview.grid_sections.map((section, idx) => (
                section.center && (
                  <Marker
                    key={idx}
                    position={section.center}
                    icon={createGridLabelIcon(`${section.label}: ~${section.count}`)}
                    interactive={false}
                  />
                )
              ))}
            </MapContainer>
          </div>
        </Card>

        {/* Grid Section Breakdown */}
        {preview.grid_sections && preview.grid_sections.length > 1 && (
          <Card className="border-0 shadow-sm">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Grid3X3 size={18} className="text-green-500" />
                Grid Section Breakdown
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {preview.grid_sections.map((section, idx) => (
                  <div 
                    key={idx} 
                    className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100"
                  >
                    <p className="font-bold text-green-600 text-lg">{section.label}</p>
                    <p className="text-sm text-gray-600">~{section.count?.toLocaleString()} {preview.delivery_type === 'apartments' ? 'doors' : 'houses'}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Approval Section */}
        {isPending && (
          <Card className="border-0 shadow-sm bg-gradient-to-r from-purple-50 to-indigo-50" data-testid="approval-section">
            <div className="p-6">
              <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
                <Send size={20} className="text-purple-600" />
                Approve This Delivery Zone
              </h3>
              <p className="text-gray-600 mb-6">
                Please review the delivery zone and demographics above. If you approve, the job will be posted live for delivery workers to accept.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label htmlFor="clientName">Your Name *</Label>
                  <Input
                    id="clientName"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Enter your name"
                    className="mt-1"
                    data-testid="client-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="clientEmail">Email (optional)</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="mt-1"
                    data-testid="client-email-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-4">
                <Button
                  onClick={() => handleApproval(true)}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                  data-testid="approve-btn"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="mr-2" />
                  )}
                  Approve for Delivery
                </Button>
                <Button
                  onClick={() => handleApproval(false)}
                  disabled={submitting}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50 py-6 text-lg"
                  data-testid="reject-btn"
                >
                  <XCircle className="mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-gray-400 pb-8">
          <p>Preview generated on {preview.created_at ? new Date(preview.created_at).toLocaleDateString() : 'N/A'}</p>
          <p className="mt-2">Powered by Local Flyer Delivery</p>
        </div>
      </div>
    </div>
  );
};

export default ZonePreviewPage;
